<template>
  <div class="w-full max-w-6xl bg-white rounded-xl shadow-lg">
    <!-- Header -->
    <div class="text-center p-8 border-b border-gray-100">
      <div class="flex justify-center mb-6">
        <img
          src="@/assets/images/efashion_Logotype_Noir 1.png"
          alt="Efashion Logo"
          class="h-12 w-auto"
        />
      </div>
      <h1 class="text-3xl font-bold text-gray-900 mb-2">Devenir Grossiste</h1>
      <p class="text-lg text-gray-600 mb-2">
        Formulaire d'inscription grossiste
      </p>
      <p class="text-gray-500">
        Rejoignez le plus grand catalogue de mode B2B en ligne de Paris !
      </p>

      <!-- Lien retour vers la connexion -->
      <div class="mt-4">
        <router-link
          to="/auth"
          class="inline-flex items-center text-sm text-blue-600 hover:text-blue-500 font-medium"
        >
          <i class="fas fa-arrow-left mr-2"></i>
          Retour à la connexion
        </router-link>
      </div>
    </div>

    <!-- En-tête du stepper -->
    <StepperHeader :steps="steps" :current-step="currentStep" />

    <!-- Légende des champs obligatoires -->
    <div class="bg-blue-50 border-b border-blue-200 px-8 pt-3">
      <div class="max-w-6xl mx-auto">
        <p
          class="text-sm text-blue-800 flex flex-col sm:flex-row items-center sm:items-start justify-center sm:justify-start"
        >
          <span class="flex items-center mb-2 sm:mb-0">
            <i class="fas fa-info-circle mr-2"></i>
            <span class="font-medium pr-1">Information :</span>
          </span>
          <span
            >Les champs marqués d'un
            <span class="text-red-500 font-bold px-1">*</span> sont
            obligatoires</span
          >
        </p>
      </div>
    </div>

    <!-- Contenu du stepper -->
    <div class="p-8">
      <!-- Formulaire d'inscription grossiste -->
      <form @submit.prevent="handleRegister">
        <!-- Étape 1: Information du Grossiste -->
        <Step
          :step-number="1"
          title="INFORMATION DU GROSSISTE"
          :is-active="currentStep === 0"
        >
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <RegisterFormField
              type="text"
              label="Dénomination société"
              required
              placeholder="Nom de votre société"
              :model-value="registerForm.companyName"
              @update:model-value="(value: any) => updateRegisterFormField('companyName', value)"
            />

            <RegisterFormField
              type="text"
              label="SIRET"
              required
              placeholder="Numéro SIRET (14 chiffres)"
              :model-value="registerForm.siret"
              @update:model-value="(value: any) => updateRegisterFormField('siret', value)"
              :error="registerForm.siret && !/^\d{14}$/.test(registerForm.siret) ?  'Le SIRET doit contenir exactement 14 chiffres' : ''"
            />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            <RegisterFormField
              type="text"
              label="Nom du contact principal"
              required
              placeholder="Nom du contact principal"
              :model-value="registerForm.contactName"
              @update:model-value="(value: any) => updateRegisterFormField('contactName', value)"
            />

            <RegisterFormField
              type="text"
              label="Prénom"
              required
              placeholder="Prénom"
              :model-value="registerForm.firstName"
              @update:model-value="(value: any) => updateRegisterFormField('firstName', value)"
            />

            <PhoneField
              label="Numéro téléphone"
              required
              placeholder="Numéro de téléphone"
              :model-value="registerForm.phone"
              @update:model-value="(value: any) => updateRegisterFormField('phone', value)"
              validationType="phone"
              @validation-changed="(isValid: boolean) => onFieldValidationChanged('phone', isValid)"
            />

            <EmailField
              label="Email"
              required
              placeholder="Email"
              :model-value="registerForm.email"
              @update:model-value="(value: any) => updateRegisterFormField('email', value)"
              @validation-changed="(isValid: boolean) => onFieldValidationChanged('email', isValid)"
            />

            <!-- Mot de passe (6 caractères minimum) -->
            <div><!-- class="md:col-span-2" -->
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe
                <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  :value="registerForm.password"
                  placeholder="Minimum 6 caractères"
                  minlength="6"
                  class="w-full h-9 px-3 pr-10 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  :class="passwordError ? 'border-red-300' : 'border-gray-300'"
                  @input="onPasswordInput"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center"
                  :aria-label="
                    showPassword
                      ? 'Masquer le mot de passe'
                      : 'Afficher le mot de passe'
                  "
                >
                  <i
                    :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                    class="text-gray-400 hover:text-gray-600"
                  ></i>
                </button>
              </div>
              <p v-if="passwordError" class="mt-1 text-xs text-red-600 mb-0">
                {{ passwordError }}
              </p>
              <p v-else class="mt-1 text-xs text-gray-500 mb-0">
                Minimum 6 caractères
              </p>
            </div>

            <!-- Confirmer le mot de passe -->
            <div><!-- class="md:col-span-2" -->
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Veuillez confirmer votre mot de passe
                <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  :value="registerForm.confirmPassword"
                  placeholder="Confirmez votre mot de passe"
                  class="w-full h-9 px-3 pr-10 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  :class="
                    confirmPasswordError ? 'border-red-300' : 'border-gray-300'
                  "
                  @input="onConfirmPasswordInput"
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center"
                  :aria-label="
                    showConfirmPassword
                      ? 'Masquer le mot de passe'
                      : 'Afficher le mot de passe'
                  "
                >
                  <i
                    :class="
                      showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'
                    "
                    class="text-gray-400 hover:text-gray-600"
                  ></i>
                </button>
              </div>
              <p v-if="confirmPasswordError" class="mt-1 text-xs text-red-600">
                {{ confirmPasswordError }}
              </p>
            </div>

            <PhoneField
              label="Téléphone portable"
              required
              placeholder="Téléphone portable"
              :model-value="registerForm.mobile"
              @update:model-value="(value: any) => updateRegisterFormField('mobile', value)"
              validationType="mobile"
              @validation-changed="(isValid: boolean) => onFieldValidationChanged('mobile', isValid)"
            />

            <PhoneField
              label="Fax (Optionnel)"
              placeholder="Numéro de fax"
              :model-value="registerForm.fax"
              @update:model-value="(value: any) => updateRegisterFormField('fax', value)"
              validationType="fax"
              @validation-changed="(isValid: boolean) => onFieldValidationChanged('fax', isValid)"
            />

            <div>
              <!-- <BrandSelectorField
                label="Nom de Marque"
                required
                placeholder="Saisir une marque..." 
                :model-value="registerForm.brand"
                @update:model-value="(value: any) => updateRegisterFormField('brand', value)"
                grid-class="md:col-span-2"
              /> -->
              <RegisterFormField
                type="text"
                label="Nom de Marque"
                required
                placeholder="Saisir une marque..."
                :model-value="registerForm.brand"
                @update:model-value="(value: any) => updateRegisterFormField('brand', value)"
              />
            </div>
            
            <DateField
              label="Date de création de la boutique"
              required
              :model-value="registerForm.creationDate"
              @update:model-value="(value: any) => updateRegisterFormField('creationDate', value)"
              help-text="Sélectionnez la date de création de votre entreprise"
              :min-date="'1900-01-01'"
              :max-date="new Date().toISOString().split('T')[0]"
              :show-validation-info="true"
              @validation-changed="onDateValidationChanged"
            />
          </div>
        </Step>

        <!-- Étape 2: Adresse du Magasin -->
        <Step
          :step-number="2"
          title="ADRESSE PRINCIPALE DU MAGASIN"
          :is-active="currentStep === 1"
        >
          <div class="space-y-6">
            <!-- Adresse principale -->
            <div>
              <!-- <h4 class="text-lg font-medium text-gray-900 mb-4">
                Adresse principale
              </h4> -->
              <AddressAutocompleteField
                :model-value="registerForm.streetAddress"
                :postal-code="registerForm.postalCode"
                :city="registerForm.city"
                @update:model-value="(value: any) => updateRegisterFormField('streetAddress', value)"
                @update:postal-code="(value: any) => updateRegisterFormField('postalCode', value)"
                @update:city="(value: any) => updateRegisterFormField('city', value)"
                @address-selected="handleAddressSelected"
                @postal-validation-changed="(isValid: boolean) => onFieldValidationChanged('postalCode', isValid)"
              />
            </div>

            <!-- Complément d'adresse -->
            <div>
              <RegisterFormField
                type="text"
                label="Complément d'adresse (Optionnel)"
                placeholder="Bâtiment , Lot , etc..."
                :model-value="registerForm.addressComplement"
                @update:model-value="(value: any) => updateRegisterFormField('addressComplement', value)"
              />
            </div>
          </div>
        </Step>

        <!-- Étape 3: Informations d'Affaires -->
        <Step
          :step-number="3"
          title="INFORMATIONS D'AFFAIRES"
          :is-active="currentStep === 2"
        >
          <div class="space-y-6">
            <!-- Documents légaux en haut -->
            <div>
              <h4 class="text-lg font-medium text-gray-900 mb-4">
                Documents légaux
              </h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FileUploadField
                  label="KBIS"
                  required
                  accept=".pdf,.jpg,.jpeg,.png"
                  help-text="Document légal de votre entreprise"
                  :model-value="registerForm.kbis"
                  @update:model-value="(value: any) => updateRegisterFormField('kbis', value)"
                  @validation-changed="(isValid: boolean) => onFieldValidationChanged('kbis', isValid)"
                />

                <FileUploadField
                  label="Pièce d'identité du gérant"
                  required
                  accept=".pdf,.jpg,.jpeg,.png"
                  help-text="Carte d'identité ou passeport du gérant"
                  :model-value="registerForm.idGerant"
                  @update:model-value="(value: any) => updateRegisterFormField('idGerant', value)"
                  @validation-changed="(isValid: boolean) => onFieldValidationChanged('idGerant', isValid)"
                />
              </div>
            </div>

            <!-- Informations bancaires en bas -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <AccountTitleField
                label="Titulaire du compte"
                required
                placeholder="Nom du titulaire du compte"
                :model-value="registerForm.accountTitle"
                @update:model-value="(value: string) => updateRegisterFormField('accountTitle', value)"
                @validation-changed="(isValid: boolean) => onFieldValidationChanged('accountTitle', isValid)"
              />

              <BankingInfoField
                label="Numéro IBAN"
                type="iban"
                required
                :model-value="registerForm.iban"
                @update:model-value="(value: string) => updateRegisterFormField('iban', value)"
                @validation-changed="(field: string, isValid: boolean) => onFieldValidationChanged(field, isValid)"
              />

              <RegisterFormField
                type="text"
                label="Code BIC"
                placeholder="Code BIC de votre banque"
                :model-value="registerForm.bic"
                @update:model-value="(value: any) => updateRegisterFormField('bic', value)"
              />

              <BankingInfoField
                label="Numéro de TVA"
                type="tva"
                required
                :model-value="registerForm.vat"
                @update:model-value="(value: string) => updateRegisterFormField('vat', value)"
                @validation-changed="(field: string, isValid: boolean) => onFieldValidationChanged(field, isValid)"
              />
            </div>
          </div>
        </Step>

        <!-- Étape 4: Détails du Business -->
        <Step
          :step-number="4"
          title="DÉTAILS DU BUSINESS"
          :is-active="currentStep === 3"
        >
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FileUploadField
              label="Logo (Optionnel)"
              accept=".jpg,.jpeg,.png"
              help-text="Logo de votre entreprise"
              :model-value="registerForm.logoFile"
              @update:model-value="(value: File | null) => updateRegisterFormField('logoFile', value)"
            />

            <ProductCategoriesField
              label="Catégories de produits"
              required
              :model-value="registerForm.productCategories"
              @update:model-value="(value: any) => updateRegisterFormField('productCategories', value)"
              @validation-changed="(isValid: boolean) => onFieldValidationChanged('productCategories', isValid)"
            />

            <RegisterFormField
              type="number"
              label="Commande minimum (€)"
              required
              placeholder="Montant minimum"
              :model-value="registerForm.minOrder"
              @update:model-value="(value: any) => updateRegisterFormField('minOrder', value)"
              @validation-changed="(isValid: boolean) => onFieldValidationChanged('minOrder', isValid)"
            />

            <RegisterFormField
              type="text"
              label="Site web (Optionnel)"
              placeholder="https://www.votre-site.com"
              :model-value="registerForm.website"
              @update:model-value="(value: any) => updateRegisterFormField('website', value)"
            />

            <PriceRangeField
              label="Gamme de prix"
              :model-value="registerForm.priceRange"
              @update:model-value="(value: PriceRange) => updateRegisterFormField('priceRange', value)"
              :min="0"
              :max="200"
              :step="10"
              grid-class="md:col-span-2"
            />

            <RegisterFormField
              type="textarea"
              label="Description (50 caractères minimum)"
              placeholder="-- Veuillez mettre votre description --"
              :model-value="registerForm.description"
              @update:model-value="(value: any) => updateRegisterFormField('description', value)"
              :rows="4"
              grid-class="md:col-span-2"
              :min-length="50"
              :max-length="510"
              required
              @validation-changed="
                onFieldValidationChanged('description', $event)
              "
            />
          </div>
        </Step>

        <!-- Étape 5: Validation finale -->
        <Step
          :step-number="5"
          title="VALIDATION FINALE"
          :is-active="currentStep === 4"
        >
          <FormSummary
            title="Résumé de votre inscription"
            subtitle="Vérifiez les informations avant de soumettre"
            :data="registerForm"
            :price-range="registerForm.priceRange"
            :banking-info="{
              iban: registerForm.iban,
              tva: registerForm.vat,
              accountTitle: registerForm.accountTitle,
            }"
            :logo-file="registerForm.logoFile"
          />

          <!-- Checkboxes -->
          <div class="space-y-4 pt-10">
            <label class="flex items-start">
              <input
                :checked="registerForm.terms"
                @change="(event: Event) => updateRegisterFormField('terms', (event.target as HTMLInputElement).checked)"
                type="checkbox"
                required
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
              />
              <span class="ml-3 text-sm text-gray-700">
                En cochant cette case, vous acceptez nos
                <a
                  href="https://www.efashion-paris.com/pages-cguv-vendeurs"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-red-500 font-medium hover:text-red-700 underline cursor-pointer"
                >
                  conditions générales d'utilisations
                </a>
              </span>
            </label>

          </div>
        </Step>

        <!-- Navigation du stepper -->
        <StepNavigation
          :current-step="currentStep"
          :total-steps="steps.length"
          :can-proceed="canProceedToNextStep"
          :can-submit="canSubmit"
          :is-loading="isLoading"
          submit-label="Créer mon compte grossiste"
          @previous="previousStep"
          @next="nextStep"
        />
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import type { PropType } from 'vue';
import {
  StepperHeader,
  Step,
  RegisterFormField,
  BrandSelectorField,
  PhoneField,
  EmailField,
  DateField,
  FileUploadField,
  ProductCategoriesField,
  PriceRangeField,
  FormSummary,
  StepNavigation,
  BankingInfoField,
  AccountTitleField,
  AddressAutocompleteField,
} from "@/components";
import type {
  SellerRegistrationFormProps,
  PriceRange,
  SellerRegisterForm,
  StepperStep,
} from "../../types/sellerRegister";

// const props = defineProps<SellerRegistrationFormProps>();
const props = defineProps({
  registerForm: {
    type: Object as PropType<SellerRegisterForm>,
    required: true,
  },
  steps: {
    type: Array as PropType<StepperStep[]>,
    required: true,
  },
  currentStep: {
    type: Number,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  canProceedToNextStep: {
    type: Boolean,
    required: true,
  },
  canSubmit: {
    type: Boolean,
    required: true,
  },
  signature: {
    type: String,
    required: false,
  },
});

// Mot de passe : affichage et validation (6 caractères min, confirmation identique)
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const passwordError = ref("");
const confirmPasswordError = ref("");

const MIN_PASSWORD_LENGTH = 6;

/**
 * Valide mot de passe et confirmation. Utilise les valeurs passées en priorité
 * pour éviter le décalage d'un caractère (props pas encore à jour après emit).
 */
function validatePasswordAndEmit(justTyped?: {
  password?: string;
  confirmPassword?: string;
}) {
  const rawPassword = justTyped?.password ?? props.registerForm.password ?? "";
  const rawConfirm =
    justTyped?.confirmPassword ?? props.registerForm.confirmPassword ?? "";
  const p = rawPassword.trim();
  const c = rawConfirm.trim();

  // Validation mot de passe : au moins 6 caractères (après trim)
  let pValid = false;
  if (!rawPassword || rawPassword.length === 0) {
    passwordError.value = "";
  } else if (p.length < MIN_PASSWORD_LENGTH) {
    passwordError.value = `Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères`;
  } else {
    passwordError.value = "";
    pValid = true;
  }

  // Validation confirmation : doit être identique au mot de passe (comparaison après trim)
  let cValid = false;
  if (!rawConfirm || rawConfirm.length === 0) {
    confirmPasswordError.value = "";
  } else if (!pValid) {
    confirmPasswordError.value =
      "Remplissez d'abord le mot de passe ci-dessus (6 caractères min.)";
  } else if (p !== c) {
    confirmPasswordError.value = "Les deux mots de passe ne correspondent pas";
  } else {
    confirmPasswordError.value = "";
    cValid = true;
  }

  emit("field-validation-changed", "password", pValid);
  emit("field-validation-changed", "confirmPassword", cValid);
}

function onPasswordInput(e: Event) {
  const value = (e.target as HTMLInputElement).value;
  updateRegisterFormField("password", value);
  validatePasswordAndEmit({ password: value });
}

function onConfirmPasswordInput(e: Event) {
  const value = (e.target as HTMLInputElement).value;
  updateRegisterFormField("confirmPassword", value);
  validatePasswordAndEmit({ confirmPassword: value });
}

const emit = defineEmits<{
  (e: "register"): void;
  (e: "previous-step"): void;
  (e: "next-step"): void;
  (e: "field-validation-changed", fieldName: string, isValid: boolean): void;
  (e: "date-validation-changed", isValid: boolean): void;
  (e: "banking-validation-changed", fieldName: string, isValid: boolean): void;
  (e: "update:register-form", value: any): void;
  (e: "update:price-range", value: any): void;
  (e: "update:banking-info", value: any): void;
  (e: "update:logo-file", value: File | null): void;
  (
    e: "address-selected",
    address        : {
      streetAddress: string;
      streetNumber : number;
      postalCode   : string;
      city         : string;
      fullAddress  : string;
    },
  ): void;
}>();

// Méthodes
const handleRegister = () => {
  emit("register");
};

const updateRegisterFormField = (fieldName: string, value: any) => {
  const updatedForm = { ...props.registerForm, [fieldName]: value };
  emit("update:register-form", updatedForm);
};

// Ces fonctions ne sont plus nécessaires car tout est maintenant dans registerForm

const handleAddressSelected = (address: {
  streetAddress: string;
  streetName   : string;
  streetNumber : string;
  postalCode   : string;
  city         : string;
  fullAddress  : string;
}) => {
  // Créer un formulaire mis à jour avec tous les champs d'adresse
  // console.log(address)
  const updatedForm = {
    ...props.registerForm,
    streetAddress : address.streetName,
    streetNumber  : address.streetNumber,
    postalCode    : address.postalCode,
    city          : address.city,
  };

  // Émettre un seul événement avec tous les champs mis à jour
  emit("update:register-form", updatedForm);
};

const previousStep = () => {
  emit("previous-step");
};

const nextStep = () => {
  emit("next-step");
};

const onFieldValidationChanged = (fieldName: string, isValid: boolean) => {
  emit("field-validation-changed", fieldName, isValid);
};

const onDateValidationChanged = (isValid: boolean) => {
  emit("date-validation-changed", isValid);
};

// Cette fonction n'est plus nécessaire car les informations bancaires sont maintenant dans registerForm
</script>
